import React, { CSSProperties, forwardRef } from 'react';
import { Sprinkles } from '@mutiny-pkg/dumpster-ui/theme/sprinkles.css';
import { Box } from '../Box';
import cx from 'classnames';
import * as style from './Flex.css';

export type FlexTerms = 0 | 1 | 2 | 3 | 4 | 5;

type FlexContainerSprinklesProps = Pick<
  Sprinkles,
  | 'alignItems'
  | 'justifyContent'
  | 'flexDirection'
  | 'placeItems'
  | 'flexWrap'
  | 'gap'
  | 'padding'
  | 'paddingX'
  | 'paddingY'
  | 'paddingTop'
  | 'paddingBottom'
  | 'paddingLeft'
  | 'paddingRight'
  | 'margin'
  | 'marginX'
  | 'marginY'
  | 'marginTop'
  | 'marginBottom'
  | 'marginLeft'
  | 'marginRight'
> & {
  flex?: FlexTerms;
};

type ResponsiveProps<Value> = {
  xs?: Value;
  sm?: Value;
  md?: Value;
  lg?: Value;
};

type ResponsiveFlexTermProps = ResponsiveProps<FlexTerms>;

type BaseProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'style' | keyof NonNullable<Sprinkles>>;

type BaseItemProps = BaseProps & {
  style?: CSSProperties;
};

export type FlexItemProps = BaseItemProps &
  ResponsiveFlexTermProps & {
    basis?: CSSProperties['flexBasis'];
    grow?: CSSProperties['flexGrow'];
    shrink?: CSSProperties['flexShrink'];
    width?: CSSProperties['width'];
    alignSelf?: CSSProperties['alignSelf'];
    justifySelf?: CSSProperties['justifySelf'];
  };

export type FlexContainerProps = FlexContainerSprinklesProps &
  BaseProps & {
    display?: 'flex' | 'inline-flex' | 'grid';
    style?: Omit<CSSProperties, 'display' | 'gap' | 'alignItems' | 'justifyContent' | 'flexWrap'>;
    fullWidth?: boolean;
    fullHeight?: boolean;
  };

// Moved out of component and exported for testing purposes
export function flexTermResolver<Value>({ xs, sm, md, lg }: ResponsiveProps<Value>, defaultValue: Value) {
  return {
    xs: xs ?? defaultValue,
    sm: sm ?? xs ?? defaultValue,
    md: md ?? sm ?? xs ?? defaultValue,
    lg: lg ?? md ?? sm ?? xs ?? defaultValue,
  };
}

export const Item = forwardRef<HTMLDivElement, FlexItemProps>(
  (
    { xs, sm, md, lg, children, basis, grow, shrink, width, alignSelf, justifySelf, style: styleProp, ...props },
    ref,
  ) => {
    const resolvedTerms = React.useMemo(() => {
      return flexTermResolver<FlexTerms>({ xs, sm, md, lg }, 0);
    }, [xs, sm, md, lg]);

    const resolvedStyle = {
      flexBasis: basis,
      flexGrow: grow,
      flexShrink: shrink,
      width,
      alignSelf,
      justifySelf,
      ...styleProp,
    };

    return (
      <Box flex={resolvedTerms.xs} className={style.item} style={resolvedStyle} ref={ref} {...props}>
        {children}
      </Box>
    );
  },
);

Item.displayName = 'Item';

export const Container = forwardRef<HTMLDivElement, FlexContainerProps>(
  (
    {
      display = 'flex',
      flexDirection = 'row',
      flexWrap = 'nowrap',
      fullWidth = false,
      fullHeight = false,
      children,
      className,
      ...props
    },
    ref,
  ) => {
    return (
      <Box
        className={cx(style.containerStyle({ fullWidth, fullHeight }), className)}
        display={display}
        flexDirection={flexDirection}
        flexWrap={flexWrap}
        ref={ref}
        {...props}
      >
        {children}
      </Box>
    );
  },
);

Container.displayName = 'Container';
